import './logo.css'

interface LogoProps {
  /** Text color for the wordmark. Defaults to inherit so the navbar can theme it. */
  color?: string;
  size?: 'small' | 'medium' | 'large';
  /** Show "By Metric Systems" credit (used on /login, /register). Default false. */
  showCredit?: boolean;
  /** Render only the mark glyph, no wordmark. */
  markOnly?: boolean;
}

/**
 * Spotprisprognos logo — a stylised lightning bolt whose zigzag doubles as a
 * rising/falling price chart. The bolt is filled with a brand gradient and
 * sits on top of a subtle baseline + ascending spark, evoking both energy
 * and a market tick. Designed to scale crisp from 16px to hero sizes.
 */
export const LogoMark: React.FC<{ size?: number; className?: string }> = ({
  size = 28,
  className,
}) => {
  const gid = 'lm-grad';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={className}
    >
      <defs>
        <linearGradient id={gid} x1="4" y1="2" x2="28" y2="30" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#2563eb" />
          <stop offset="55%"  stopColor="#7c3aed" />
          <stop offset="100%" stopColor="#10b981" />
        </linearGradient>
      </defs>

      {/* Faint baseline ticks — reads as a chart axis under the bolt */}
      <path
        d="M3 27.5 L9 25.5 L15 22.5 L21 18.5 L27 13.5"
        stroke={`url(#${gid})`}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.28"
      />

      {/* Lightning bolt — sharp, modern, slightly tilted into the up-and-to-the-right
          motion of the chart line above. Two-segment Z with a clean inner notch. */}
      <path
        d="M18.5 2.2
           L7.2 17.4
           a0.9 0.9 0 0 0 0.72 1.44
           H14.1
           L11.6 29.1
           a0.6 0.6 0 0 0 1.06 0.5
           L24.86 14.5
           a0.9 0.9 0 0 0 -0.72 -1.44
           H17.9
           L20.2 3.4
           a0.6 0.6 0 0 0 -1.7 -1.2 z"
        fill={`url(#${gid})`}
      />
    </svg>
  );
};

export const Logo: React.FC<LogoProps> = ({
  color,
  size = 'medium',
  showCredit = false,
  markOnly = false,
}) => {
  const sizes = {
    small:  { mark: 24, word: '1.02rem', credit: '0.7rem',  gap: 8 },
    medium: { mark: 32, word: '1.25rem', credit: '0.75rem', gap: 10 },
    large:  { mark: 40, word: '1.5rem',  credit: '0.8rem',  gap: 12 },
  } as const;
  const s = sizes[size];

  if (markOnly) {
    return <LogoMark size={s.mark} className="logo-mark" />;
  }

  return (
    <div className="logo-v2" style={{ gap: s.gap, color }}>
      <LogoMark size={s.mark} className="logo-mark" />
      <div className="logo-text">
        <span className="logo-word" style={{ fontSize: s.word, color }}>
          Spotprisprognos
        </span>
        {showCredit && (
          <span className="logo-credit" style={{ fontSize: s.credit }}>
            by <a href="https://metricsystems.se">Metric Systems</a>
          </span>
        )}
      </div>
    </div>
  );
};
